// Import Libraries
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

// Import Components
import SelectPlan from '../../Fragments/Subscribe/SelectPlan.js';
import ProcessSubCharge from '../../Fragments/Payments/ProcessSubCharge.js';
import Confirmation from '../../Fragments/Payments/Confirmation.js';

// Import Services
import paymentService from '../../../services/paymentService.js';
import userService from '../../../services/userService.js';

// Context
import { UserContext } from '../../../context/User.js';

// Setup Modal to Root
Modal.setAppElement('#root');

const UpgradeMembership = ({
  featureMsg,
  isOpen,
  onRequestClose,
  includeFree,
  userDetails,
  currentSubscription,
  scriptId,
  startingStep = 1,
}) => {
  const { user, setUpdateUser } = useContext(UserContext);

  const [orderInfo, setOrderInfo] = useState({
    pricing: '2',
    includeTrial: false,
    plans: [],
    amountDue: '0.00',
    selectedPlan: {},
    dueDate: null,
    couponCode: null,
    discount: 0,
  });

  const [step, setStep] = useState(startingStep);
  const site = window.location.origin;

  // Prefer passed-in userDetails if present (e.g. staff/admin viewing), else context user.
  const baseUser = userDetails || user;

  const userId = baseUser?.id || null;

  // Local state for fetched data — DO NOT mutate baseUser directly.
  const [cards, setCards] = useState([]);
  const [invoiceDue, setInvoiceDue] = useState(null);

  // Provide a stable object to children
  const mergedUserDetails = useMemo(() => {
    if (!baseUser) return baseUser;
    return { ...baseUser, cards, invoiceDue };
  }, [baseUser, cards, invoiceDue]);

  // -------------------------
  // Logging helpers
  // -------------------------
  const modalSessionIdRef = useRef(0);
  const lastLoggedKeyRef = useRef(null);

  const getLoggingUserId = () => userId || user?.id || null;

  const stepName = (s) => {
    switch (s) {
      case 1:
        return 'select_plan';
      case 2:
        return 'process_sub_charge';
      case 4:
        return 'confirmation';
      default:
        return `step_${s}`;
    }
  };

  const safeStringify = (obj) => {
    try {
      return JSON.stringify(obj);
    } catch (_e) {
      return null;
    }
  };

  const logStepView = async (s, extraNotes = null) => {
    const loggingUserId = getLoggingUserId();
    if (!isOpen || !loggingUserId) return;

    const key = `${modalSessionIdRef.current}:${s}`;
    if (lastLoggedKeyRef.current === key) return;

    lastLoggedKeyRef.current = key;

    const action = `UpgradeMembership - Viewed Step ${s} (${stepName(s)})`;

    const notes = safeStringify({
      featureMsg: featureMsg || null,
      includeFree: !!includeFree,
      scriptId: scriptId || null,
      currentSubscription: currentSubscription || null,
      site: site || null,
      step: s,
      stepName: stepName(s),
      pricing: orderInfo?.pricing || null,
      selectedPlanId: orderInfo?.selectedPlan?.details?.id || null,
      amountDue: orderInfo?.amountDue || null,
      dueDate: orderInfo?.dueDate
        ? new Date(orderInfo.dueDate).toISOString?.() || orderInfo.dueDate
        : null,
      extra: extraNotes || null,
    });

    try {
      // performedBy uses context user.id when available
      await userService.addActionLog(action, loggingUserId, 'user_id', notes, user?.id);
    } catch (_e) {
      // swallow errors; never block modal UX
    }
  };

  // -------------------------
  // Step movement
  // -------------------------
  const moveToStep = async (nextStep) => {
    // Guard: steps 1 & 2 require selectedPlan id
    if (nextStep === 1 || nextStep === 2) {
      if (!orderInfo.selectedPlan?.details?.id) return;
    }

    if (nextStep === 4) {
      setUpdateUser('subscription');
    }

    setStep(nextStep);

    if (nextStep === 5) {
      window.location.reload();
    }
  };

  const onClose = () => {
    if (step === 4) {
      window.location.reload();
    } else {
      onRequestClose(false);
    }
  };

  // -------------------------
  // Modal open/initialize
  // -------------------------
  useEffect(() => {
    if (!isOpen) return;

    modalSessionIdRef.current += 1;
    lastLoggedKeyRef.current = null;

    // reset to step 1 on open
    setStep(1);

    // ensure due date
    setOrderInfo((prev) => {
      if (prev?.dueDate) return prev;
      const dueDate = new Date();
      dueDate.setMonth(dueDate.getMonth() + 1);
      return { ...prev, dueDate };
    });
  }, [isOpen]);

  // -------------------------
  // Fetch cards + invoice due (ONLY when modal open + userId changes)
  // -------------------------
  useEffect(() => {
    if (!isOpen || !userId) return;

    let cancelled = false;

    (async () => {
      try {
        const resp = await paymentService.getAvailableCards(userId);

        // Normalize to the same format ProcessSubCharge expects:
        // it can accept resp.paymentMethods or an array.
        const paymentMethods = resp?.paymentMethods ?? (Array.isArray(resp) ? resp : []);

        if (!cancelled) setCards(paymentMethods || []);
      } catch (_e) {
        if (!cancelled) setCards([]);
      }

      try {
        const inv = await paymentService.checkForPaymentsDue(userId);
        if (!cancelled) setInvoiceDue(inv ?? null);
      } catch (_e) {
        if (!cancelled) setInvoiceDue(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, userId]);

  // -------------------------
  // Wix labels
  // -------------------------
  useEffect(() => {
    const loggingUserId = getLoggingUserId();
    if (!isOpen || !loggingUserId) return;

    try {
      if (step === 1) userService.UpdateWixLabel(loggingUserId, 'custom.viewed-plan', true);
      if (step === 2) userService.UpdateWixLabel(loggingUserId, 'custom.selected-plan', true);
    } catch (_e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, userId]);

  // -------------------------
  // Log whenever a step is shown
  // -------------------------
  useEffect(() => {
    if (!isOpen) return;
    if (step === 1 || step === 2 || step === 4) {
      logStepView(step);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step]);

  return (
    <>
      <Modal isOpen={isOpen} contentLabel="Idle Modal" className="modal wide">
        <div
          className="close-button"
          role="button"
          onClick={() => onClose()}
          style={{ margin: '10px' }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div>
          {step === 1 ? (
            <SelectPlan
              featureMsg={featureMsg}
              includeFree={includeFree}
              nextStep={moveToStep}
              currentSubscription={currentSubscription}
              scriptId={scriptId}
              orderInfo={orderInfo}
              setOrderInfo={setOrderInfo}
            />
          ) : step === 2 ? (
            <ProcessSubCharge
              scriptId={scriptId}
              nextStep={moveToStep}
              userDetails={mergedUserDetails}
              orderInfo={orderInfo}
              setOrderInfo={setOrderInfo}
            />
          ) : step === 4 ? (
            <Confirmation
              scriptId={scriptId}
              nextStep={onClose}
              currentSubscription={currentSubscription}
              orderInfo={orderInfo}
            />
          ) : null}
        </div>

        {step < 4 ? (
          <div className="FullWidthFlex" style={{ paddingTop: '30px', marginBottom: '-20px' }}>
            <span className="FivePortionFlex">&nbsp;</span>

            <span
              className="OnePortionFlex buttonClone"
              style={{ height: '5px', backgroundColor: step === 1 ? '#a899a8' : '' }}
              onClick={() => moveToStep(1)}
            ></span>

            <span
              className="OnePortionFlex buttonClone"
              style={{ height: '5px', backgroundColor: step === 2 ? '#a899a8' : '' }}
            ></span>

            <span className="FivePortionFlex">&nbsp;</span>
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default UpgradeMembership;
